import { CalendarRange, Clock, Minus, Moon, MoonStar, Plus, Sun, Sunrise } from 'lucide-react'
import type { ComponentType, KeyboardEvent, SVGProps } from 'react'
import { Button } from '@/shared/ui/primitives/button'
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/primitives'

export type HuntTimePeriodValue = 'am' | 'midday' | 'pm' | 'all-day' | 'night'

export const RETIRED_OPTION_SUFFIX = 'no longer offered'

type PeriodConfig = {
  value: HuntTimePeriodValue
  label: string
  from: string
  to: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  retired?: boolean
}

const ALL_PERIODS: PeriodConfig[] = [
  { value: 'am', label: 'AM', from: 'First Light', to: '10:00 AM', icon: Sunrise },
  { value: 'midday', label: 'Midday', from: '10:00 AM', to: '2:00 PM', icon: Sun },
  { value: 'pm', label: 'PM', from: '2:00 PM', to: 'Sunset', icon: Moon },
  { value: 'all-day', label: 'All Day', from: 'First Light', to: 'Sunset', icon: Clock },
  { value: 'night', label: 'Night', from: 'Sunset', to: 'First Light', icon: MoonStar },
]

const DEFAULT_MIN_DURATION_DAYS = 2
const DEFAULT_MAX_DURATION_DAYS = 14
const COLUMN_GAP_REM = 0.75

export type HuntTimeBlockOption = {
  timePeriod: HuntTimePeriodValue
  label: string
  from: string
  to: string
}

function toPeriodConfig(option: HuntTimeBlockOption): PeriodConfig {
  const platformPeriod = ALL_PERIODS.find((p) => p.value === option.timePeriod)
  return {
    value: option.timePeriod,
    label: option.label,
    from: option.from,
    to: option.to,
    icon: platformPeriod?.icon ?? Clock,
  }
}

function toRetiredPeriodConfig(value: HuntTimePeriodValue): PeriodConfig {
  const platformPeriod = ALL_PERIODS.find((p) => p.value === value)
  return {
    value,
    label: `${platformPeriod?.label ?? value} (${RETIRED_OPTION_SUFFIX})`,
    from: platformPeriod?.from ?? '',
    to: platformPeriod?.to ?? '',
    icon: platformPeriod?.icon ?? Clock,
    retired: true,
  }
}

export type HuntTimePeriodSelectorProps = {
  value: HuntTimePeriodValue | undefined
  onChange: (value: HuntTimePeriodValue) => void
  visiblePeriods?: HuntTimePeriodValue[]
  timeBlocks?: HuntTimeBlockOption[]
  keepRetiredValue?: boolean
  disabled?: boolean
  compact?: boolean
  showDays?: boolean
  isMultiDayActive?: boolean
  onToggleMultiDay?: () => void
  durationDays?: number
  onDurationChange?: (days: number) => void
  minDurationDays?: number
  maxDurationDays?: number
}

export function HuntTimePeriodSelector({
  value,
  onChange,
  visiblePeriods,
  timeBlocks,
  keepRetiredValue,
  disabled,
  compact = false,
  showDays,
  isMultiDayActive,
  onToggleMultiDay,
  durationDays,
  onDurationChange,
  minDurationDays,
  maxDurationDays,
}: HuntTimePeriodSelectorProps) {
  const defaultPeriods = visiblePeriods
    ? ALL_PERIODS.filter((p) => visiblePeriods.includes(p.value))
    : ALL_PERIODS
  const configuredPeriods =
    timeBlocks && timeBlocks.length > 0 ? timeBlocks.map(toPeriodConfig) : null
  const offeredPeriods = configuredPeriods ?? defaultPeriods
  const retiredPeriod =
    keepRetiredValue &&
    value !== undefined &&
    !offeredPeriods.some((period) => period.value === value)
      ? toRetiredPeriodConfig(value)
      : null
  const periods = retiredPeriod
    ? [...offeredPeriods, retiredPeriod]
    : offeredPeriods

  const minDays = minDurationDays ?? DEFAULT_MIN_DURATION_DAYS
  const maxDays = maxDurationDays ?? DEFAULT_MAX_DURATION_DAYS
  const currentDays = durationDays ?? minDays
  const daysColumn = showDays ? 1 : 0
  const renderedColumns = periods.length + daysColumn
  const columnBasis =
    Math.max(periods.length, defaultPeriods.length) + daysColumn
  const trackWidth = `calc((100% - ${(columnBasis - 1) * COLUMN_GAP_REM}rem) / ${columnBasis})`

  if (compact) {
    return (
      <ToggleGroup
        type="single"
        variant="card"
        size="sm"
        value={value ?? ''}
        onValueChange={(v) => {
          if (!v) return
          onChange(v as HuntTimePeriodValue)
        }}
        disabled={disabled}
        className="flex-wrap justify-start"
      >
        {periods.map((period) => {
          const Icon = period.icon
          return (
            <ToggleGroupItem
              key={period.value}
              value={period.value}
              aria-label={period.label}
              disabled={period.retired}
              className="gap-1.5"
            >
              <Icon className="size-3.5" aria-hidden="true" />
              <span className="text-xs font-semibold">{period.label}</span>
              {period.from && period.to ? (
                <span className="text-xs text-muted-foreground">
                  {`${period.from}–${period.to}`}
                </span>
              ) : null}
            </ToggleGroupItem>
          )
        })}
      </ToggleGroup>
    )
  }

  const handleDaysToggle = () => {
    if (!isMultiDayActive) onChange('all-day')
    onToggleMultiDay?.()
  }

  return (
    <div
      className="flex w-full min-w-0 snap-x snap-mandatory items-stretch justify-start gap-3 overflow-x-auto lg:grid lg:overflow-visible lg:justify-center"
      style={{
        gridTemplateColumns: `repeat(${renderedColumns}, ${trackWidth})`,
      }}
    >
      <ToggleGroup
        type="single"
        variant="card"
        value={isMultiDayActive ? '' : value ?? ''}
        onValueChange={(v) => {
          if (!v) return
          if (isMultiDayActive) onToggleMultiDay?.()
          onChange(v as HuntTimePeriodValue)
        }}
        disabled={disabled}
        className="contents"
      >
        {periods.map((period) => {
          const Icon = period.icon
          return (
            <ToggleGroupItem
              key={period.value}
              value={period.value}
              aria-label={period.label}
              disabled={period.retired}
              className="flex h-auto min-w-[8.5rem] shrink-0 snap-start flex-col items-center justify-start gap-1.5 px-2 py-3 lg:min-w-0 lg:shrink"
            >
              <Icon className="size-5" aria-hidden="true" />
              <span className="text-xs font-semibold">{period.label}</span>
              <div className="text-xs leading-tight text-muted-foreground">
                <span>{period.from}</span>
                <br />
                <span className="italic">til</span>
                <br />
                <span>{period.to}</span>
              </div>
            </ToggleGroupItem>
          )
        })}
      </ToggleGroup>
      {showDays ? (
        <DaysCard
          isMultiDayActive={isMultiDayActive ?? false}
          onToggleMultiDay={handleDaysToggle}
          onDurationChange={onDurationChange}
          currentDays={currentDays}
          minDays={minDays}
          maxDays={maxDays}
          disabled={disabled}
        />
      ) : null}
    </div>
  )
}

type DaysCardProps = {
  isMultiDayActive: boolean
  onToggleMultiDay?: () => void
  onDurationChange?: (days: number) => void
  currentDays: number
  minDays: number
  maxDays: number
  disabled?: boolean
}

function DaysCard({
  isMultiDayActive,
  onToggleMultiDay,
  onDurationChange,
  currentDays,
  minDays,
  maxDays,
  disabled,
}: DaysCardProps) {
  const handleToggle = () => {
    if (disabled) return
    onToggleMultiDay?.()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') return
    event.preventDefault()
    handleToggle()
  }

  return (
    <div
      role="switch"
      tabIndex={disabled ? -1 : 0}
      aria-checked={isMultiDayActive}
      aria-label="Days"
      aria-disabled={disabled || undefined}
      data-state={isMultiDayActive ? 'on' : 'off'}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className="inline-flex h-auto min-w-[8.5rem] shrink-0 cursor-pointer snap-start flex-col items-center justify-start gap-1.5 whitespace-nowrap rounded-md border bg-card px-2 py-3 text-sm font-medium text-card-foreground ring-offset-background transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[state=on]:border-primary data-[state=on]:bg-primary/5 lg:min-w-0 lg:shrink"
    >
      <CalendarRange className="size-5" aria-hidden="true" />
      <span className="text-xs font-semibold">Days</span>
      {isMultiDayActive ? (
        <div className="mt-1 flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={(event) => {
              event.stopPropagation()
              onDurationChange?.(currentDays - 1)
            }}
            disabled={currentDays <= minDays}
            aria-label="Decrease days"
          >
            <Minus />
          </Button>
          <span className="min-w-[2.5rem] text-center text-xs tabular-nums">
            {currentDays} days
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={(event) => {
              event.stopPropagation()
              onDurationChange?.(currentDays + 1)
            }}
            disabled={currentDays >= maxDays}
            aria-label="Increase days"
          >
            <Plus />
          </Button>
        </div>
      ) : null}
    </div>
  )
}
